/* eslint-disable react/no-array-index-key */
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import {
  AssetLinkBlock,
  BLOCKS,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Heading6,
  OrderedList,
  Paragraph,
  Quote,
  Table as TableBlock,
  Text,
} from '@contentful/rich-text-types';
import { LandingPageContent } from '@website/components/LandingPages/LandingPageContentType';
import LandingPageFAQs from '@website/components/LandingPages/LandingPageFAQs';
import { getImageUrl } from '@website/components/media/utils/imgix';
import SeoMetaTags from '@website/components/seoMetaTags';
import { TripsSearchResultsContent } from '@website/components/trips/search/TripsSearchResults';
import LoadingPage from '@website/components/utilities/LoadingPage';
import NotFound from '@website/components/utilities/NotFound';
import { TripSearchIndex } from '@website/types/TripSearchIndex';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import {
  Breadcrumb,
  Button,
  Col,
  Container,
  Row,
  Table,
} from 'react-bootstrap';
import Imgix from 'react-imgix';
import WhySkyhook from '../about/WhySkyhook';
import HeaderSection from '../HeaderSection';
import { SalesPopupProvider } from '../SalesPopupProvider';
import TrustPilotWidget from '../trustpilot/TrustPilotWidget';

type LandingPageProps = {
  trips: TripSearchIndex[];
  page: LandingPageContent;
  totalPages: number;
  filterBy: string;
  filterByValue: string;
};

export default function LandingPage({
  page,
  trips,
  totalPages,
  filterBy,
  filterByValue,
}: LandingPageProps) {
  const router = useRouter();

  const loadMoreTrips = useCallback(async () => {
    if (filterBy === 'activities') {
      router.push(`/trips?activities=${filterByValue}`);
      return;
    }
    if (filterBy === 'country') {
      router.push(`/trips?country=${filterByValue}`);
      return;
    }
    router.push(`/trips?query=${filterByValue}`);
  }, [filterBy, filterByValue, router]);

  // show loading page if the page hasn't been generated, fallback will generate
  // send it to the frontend
  if (router.isFallback) {
    return <LoadingPage />;
  }

  if (!page) return <NotFound />;

  // Add inline image rendering with Imgix
  const dtrOptions = {
    renderNode: {
      // Text
      [BLOCKS.PARAGRAPH]: (node: Paragraph) => {
        return (
          <Row>
            <Col xs={12} className="mx-auto">
              {documentToReactComponents(node as any)}
            </Col>
          </Row>
        );
      },
      [BLOCKS.HEADING_1]: (node: Heading1) => {
        return (
          <Row>
            <Col xs={12} sm={8} className="mx-auto mb-3">
              {node.content.map((content: Text, idx) => (
                <h1 key={idx}>{content.value}</h1>
              ))}
            </Col>
          </Row>
        );
      },
      [BLOCKS.HEADING_2]: (node: Heading2) => {
        return (
          <Row>
            <Col xs={12} className="mx-auto mt-3 mb-1">
              {node.content.map((content: Text, idx) => (
                <h2 key={idx}>{content.value}</h2>
              ))}
            </Col>
          </Row>
        );
      },
      [BLOCKS.HEADING_3]: (node: Heading3) => {
        return (
          <Row>
            <Col xs={12} className="mx-auto">
              {node.content.map((content: Text, idx) => (
                <h3 key={idx}>{content.value}</h3>
              ))}
            </Col>
          </Row>
        );
      },
      [BLOCKS.HEADING_4]: (node: Heading4) => {
        return (
          <Row>
            <Col xs={12} className="mx-auto">
              {node.content.map((content: Text, idx) => (
                <h4 key={idx}>{content.value}</h4>
              ))}
            </Col>
          </Row>
        );
      },
      [BLOCKS.HEADING_5]: (node: Heading5) => {
        return (
          <Row>
            <Col xs={12} className="mx-auto">
              {node.content.map((content: Text, idx) => (
                <h5 key={idx}>{content.value}</h5>
              ))}
            </Col>
          </Row>
        );
      },
      [BLOCKS.HEADING_6]: (node: Heading6) => {
        return (
          <Row>
            <Col xs={12} className="mx-auto">
              {node.content.map((content: Text, idx) => (
                <h6 key={idx}>{content.value}</h6>
              ))}
            </Col>
          </Row>
        );
      },
      [BLOCKS.QUOTE]: (node: Quote) => {
        return (
          <Row>
            <Col xs={12} className="mx-auto">
              {node.content.map((paragraph: Paragraph, idx) => (
                <blockquote key={idx} className="blockquote">
                  {documentToReactComponents(paragraph as any)}
                </blockquote>
              ))}
            </Col>
          </Row>
        );
      },
      [BLOCKS.OL_LIST]: (node: OrderedList) => {
        return (
          <Row>
            <Col xs={12} className="mx-auto">
              {documentToReactComponents(node as any)}
            </Col>
          </Row>
        );
      },
      [BLOCKS.UL_LIST]: (node: OrderedList) => {
        return (
          <Row>
            <Col xs={12} className="mx-auto">
              {documentToReactComponents(node as any)}
            </Col>
          </Row>
        );
      },
      // Tables
      [BLOCKS.TABLE]: (node: TableBlock) => {
        const rows = node.content;
        const headerRows = rows.filter(
          (row) => row.content[0]?.nodeType === 'table-header-cell',
        );
        const otherRows = rows.filter(
          (row) => row.content[0]?.nodeType !== 'table-header-cell',
        );

        return (
          <Row>
            <Col className="mx-auto mt-3 mb-4">
              <Table striped responsive="xs" hover size="sm">
                <thead>
                  {headerRows.map((row) =>
                    documentToReactComponents(row as any),
                  )}
                </thead>
                <tbody>
                  {otherRows.map((row) =>
                    documentToReactComponents(row as any),
                  )}
                </tbody>
              </Table>
            </Col>
          </Row>
        );
      },
      // Images
      [BLOCKS.EMBEDDED_ASSET]: (node: AssetLinkBlock) => {
        // Get the image details
        const { id } = node.data.target.sys;
        const image = (page.snapshot.links.assets.block as any).find(
          (i) => i.sys.id === id,
        );

        // Return if it doesn't have a URL (this can happen in draft mode)
        if (!image.url) return null;

        // Return the image in a full-width row
        return (
          <Row className="my-3">
            <Col className="text-center">
              <figure>
                <Imgix
                  src={`${getImageUrl(
                    image.url,
                    process.env.NEXT_PUBLIC_CONTENTFUL_IMGIX_URL,
                  )}`}
                  imgixParams={{
                    auto: 'compress,format,enhance,redeye',
                  }}
                  sizes="(min-width: 1400px) 1296px, (min-width: 1200px) 1116px, (min-width: 992px) 936px, (min-width: 768px) 696px, (min-width: 576px) 516px, 98vw"
                  className="figure-img img-fluid"
                  htmlAttributes={{
                    alt: image.title || image.description || '',
                    style: {
                      maxHeight: '90vh',
                    },
                  }}
                />
                {image.description && (
                  <figcaption className="figure-caption text-center">
                    {image.description}
                  </figcaption>
                )}
              </figure>
            </Col>
          </Row>
        );
      },
    },
  };

  return (
    <>
      <SeoMetaTags
        title={page.metaTitle ?? page.title}
        description={page.metaDescription ?? page.description}
        keywords={page.metaKeywords ?? []}
        showWebsiteName={false}
        imageKey={page.image.url}
        cdn={process.env.NEXT_PUBLIC_CONTENTFUL_IMGIX_URL}
      />

      <HeaderSection title={page.title} image={page.image.url} />

      <style jsx>{`
        .breadcrumb {
          font-size: 16px;
        }
      `}</style>

      <section className="py-4 bg-gray-100">
        <Container>
          <TrustPilotWidget
            templateId="5419b6ffb0d04a076446a9af"
            height="20px"
            className="mx-auto"
          />
        </Container>
      </section>

      <section>
        <Container className="pt-2">
          <Breadcrumb className="py-2">
            <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
            <Breadcrumb.Item active>{page.title}</Breadcrumb.Item>
          </Breadcrumb>
          <p className="lead">{page.description}</p>
        </Container>
      </section>

      <section>
        <Container className="py-2">
          <h2>{page.tripsHeaderLabel ?? 'Popular Trips'}</h2>
          <TripsSearchResultsContent hits={trips} />
          {totalPages > 1 && (
            <Col className="text-center py-4">
              <Button variant="primary" onClick={loadMoreTrips}>
                Load More
              </Button>
            </Col>
          )}
        </Container>
      </section>

      <section>
        <Container className="py-2">
          <h2>{page.snapshotLabel ?? 'Snapshot'}</h2>
          <p>{documentToReactComponents(page.snapshot.json, dtrOptions)}</p>
        </Container>
      </section>

      <LandingPageFAQs
        faqs={page.faqsCollection.items}
        faqsHeaderLabel={page.faqsHeaderLabel ?? 'FAQs'}
        dtrOptions={dtrOptions}
      />

      <WhySkyhook />

      <SalesPopupProvider bottomPosition={10} />
    </>
  );
}
